'use client'

import { type FC } from 'react'

import { SelectOption } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../_providers/TranslationProvider.client'

import { type ParsedSizeChart } from './sizeChartData'
import * as Styles from './SizeChartSizeTable.styles'

const MISSING_VALUE = '—'

export type SizeChartSizeTableProps = {
  chart: ParsedSizeChart
  selectedRegion: string
  onRegionChange: (region: string) => void
  highlightedSize: string
}

export const SizeChartSizeTable: FC<SizeChartSizeTableProps> = ({
  chart,
  selectedRegion,
  onRegionChange,
  highlightedSize,
}) => {
  const {
    sizesSection: { sizeChart: strings },
  } = useTranslation()
  const { measurementColumns, regionColumns } = chart
  const regionColumn = regionColumns.find(column => column.label === selectedRegion)

  return (
    <Styles.ScrollShadowWrap>
      <Styles.SizeTableWrap>
        <Styles.Table>
          <thead>
            <Styles.Row>
              <Styles.HeaderCell scope='col'>{chart.sizeLabel}</Styles.HeaderCell>
              {measurementColumns.map(column => (
                <Styles.HeaderCell key={column.label} scope='col'>
                  {column.label}
                </Styles.HeaderCell>
              ))}
              {regionColumns.length > 0 && (
                <Styles.HeaderCell scope='col'>
                  <Styles.RegionSelect
                    aria-label={strings.regionSelectLabel}
                    isInvalid={false}
                    value={selectedRegion}
                    onChange={onRegionChange}
                    renderSelectValue={value => value}
                  >
                    {regionColumns.map(column => (
                      <SelectOption key={column.label} value={column.label}>
                        {column.label}
                      </SelectOption>
                    ))}
                  </Styles.RegionSelect>
                </Styles.HeaderCell>
              )}
            </Styles.Row>
          </thead>
          <tbody>
            {chart.sizes.map((size, sizeIndex) => (
              <Styles.Row key={size} $isHighlighted={size === highlightedSize}>
                <Styles.SizeCell scope='row'>{size}</Styles.SizeCell>
                {measurementColumns.map(column => (
                  <Styles.Cell key={column.label}>
                    {column.values[sizeIndex] ?? MISSING_VALUE}
                  </Styles.Cell>
                ))}
                {regionColumns.length > 0 && (
                  <Styles.Cell>{regionColumn?.values[sizeIndex] ?? MISSING_VALUE}</Styles.Cell>
                )}
              </Styles.Row>
            ))}
          </tbody>
        </Styles.Table>
      </Styles.SizeTableWrap>
    </Styles.ScrollShadowWrap>
  )
}
